/**
 * Spec 202 / 204 — typed client for the source-classification repair route.
 *
 * GEO Observatory's domain classifier recognised no Danish or European institution, so
 * every Danish national authority classified as `other` — the same weight as an
 * anonymous blog. Spec 202 fixed the rules and shipped this route; 204 is what
 * lets anyone actually run it.
 *
 * Fixing the rules alone corrects NOTHING that already exists, because three
 * layers cache the old answer:
 *
 *   source_domains.type                CACHED     — classified only on INSERT
 *     ↓ DEFAULT_AUTHORITY[type]
 *   response_analyses.authority_score  STORED     — written once at analysis
 *     ↓ AVG(ra.authority_score)
 *   visibility_scores.quality_score    AGGREGATED — never joins source_domains
 *
 * Hence three phases, each capping its own unit of work (a source row, a
 * response, a score bucket) and driven in order from here:
 *
 *   1. reclassify — re-label cached `other` rows against the corrected rules.
 *                   Tenant-wide; no brand scope exists for a domain cache.
 *   2. reanalyze  — recompute stored authority for the affected responses.
 *                   THE INVISIBLE PHASE: skipping it leaves the screen exactly
 *                   as it was, which is precisely how spec 199 shipped a correct
 *                   correction with nil user-visible effect and needed spec 201
 *                   to deliver.
 *   3. rescore    — recompute the affected score buckets, all history, not just
 *                   today.
 *
 * The order is a protocol invariant and comes from the server as `next_phase`;
 * we follow that rather than hardcoding the sequence, so a server that ever
 * reorders or inserts a phase is obeyed instead of silently half-run.
 *
 *   POST /api/v1/admin/backfill/domain-classification
 *     reclassify: { limit?, after_created_at?, after_id? }
 *     reanalyze:  { brand_id?, after_id?, limit? }
 *     rescore:    { brand_id?, after_brand_id?, after_period?, period_limit? }
 *
 * Admin-gated server-side; every phase idempotent and resumable from the start
 * of its own cursor.
 */
import { apiFetch } from '../lib/api.js';

export type DomainClassificationPhase = 'reclassify' | 'reanalyze' | 'rescore';

/** Phase 1 — keyset over `source_domains` via `(created_at, id)`. */
export interface ReclassifyBatchResult {
  phase: 'reclassify';
  scanned: number;
  reclassified: number;
  by_type?: Record<string, number>;
  /**
   * Rows whose label was corrected but whose score will NOT move, because
   * `source_domains.authority_score` is set and overrides the classification
   * outright. Reported rather than swallowed: without it, a run that behaved
   * correctly is indistinguishable from one that did nothing.
   */
  overridden: number;
  last_created_at: string | null;
  last_id: string | null;
  done: boolean;
  remaining: number;
  next_phase?: DomainClassificationPhase;
}

/** Phase 2 — keyset over affected responses via `last_id`. */
export interface ReanalyzeBatchResult {
  phase: 'reanalyze';
  processed: number;
  responses_reanalyzed: number;
  responses_failed: number;
  brand_metrics_moved: number;
  last_id: string | null;
  done: boolean;
  remaining: number;
  brands: string[];
  next_phase?: DomainClassificationPhase;
}

/** Phase 3 — composite keyset over `(period, brand_id)`. */
export interface RescoreBatchResult {
  phase: 'rescore';
  periods_rescored: number;
  periods_failed: number;
  /**
   * Score rows actually written — NOT a restatement of `periods_rescored`,
   * which counts buckets ATTEMPTED. A day whose analyses never completed is
   * walked, aggregates to nothing, writes nothing, and still counts as
   * rescored. Without this second number, "40 days updated, nothing written"
   * reads as a clean run.
   */
  score_rows_written: number;
  last_brand_id: string | null;
  last_period: string | null;
  done: boolean;
  periods_remaining: number;
  budget_stopped: boolean;
  brands: string[];
  next_phase?: DomainClassificationPhase;
}

export type DomainClassificationAnyBatchResult =
  | ReclassifyBatchResult
  | ReanalyzeBatchResult
  | RescoreBatchResult;

/** Accumulated totals across the whole three-phase run. */
export interface DomainClassificationRepairResult {
  scanned: number;
  reclassified: number;
  overridden: number;
  responses_reanalyzed: number;
  responses_failed: number;
  periods_rescored: number;
  periods_failed: number;
  score_rows_written: number;
  phasesRun: DomainClassificationPhase[];
  batches: number;
}

export interface DomainClassificationProgress {
  phase: DomainClassificationPhase;
  /** 1-based position of the running phase, for "part N of 3". */
  phaseIndex: number;
  reclassified: number;
  responsesReanalyzed: number;
  periodsRescored: number;
  /** Items still queued after the running phase's cursor. */
  remaining: number;
}

async function postPhase<T>(body: Record<string, unknown>): Promise<T> {
  const res = await apiFetch('/api/v1/admin/backfill/domain-classification', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Source classification repair failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: T };
  return json.data;
}

export function reclassifyBatch(
  afterCreatedAt?: string | null,
  afterId?: string | null,
  limit?: number,
): Promise<ReclassifyBatchResult> {
  const body: Record<string, unknown> = { phase: 'reclassify' };
  if (afterCreatedAt) body.after_created_at = afterCreatedAt;
  if (afterId) body.after_id = afterId;
  if (limit) body.limit = limit;
  return postPhase<ReclassifyBatchResult>(body);
}

export function reanalyzeBatch(
  brandId: string,
  afterId?: string | null,
  limit?: number,
): Promise<ReanalyzeBatchResult> {
  const body: Record<string, unknown> = { phase: 'reanalyze', brand_id: brandId };
  if (afterId) body.after_id = afterId;
  if (limit) body.limit = limit;
  return postPhase<ReanalyzeBatchResult>(body);
}

export function rescoreBatch(
  brandId: string,
  afterPeriod?: string | null,
  afterBrandId?: string | null,
  periodLimit?: number,
): Promise<RescoreBatchResult> {
  const body: Record<string, unknown> = { phase: 'rescore', brand_id: brandId };
  if (afterPeriod) body.after_period = afterPeriod;
  if (afterBrandId) body.after_brand_id = afterBrandId;
  if (periodLimit) body.period_limit = periodLimit;
  return postPhase<RescoreBatchResult>(body);
}

/**
 * Per-phase request caps. Deliberately NOT a shared budget: spec 198/201
 * established that one counter across phases lets a large early phase exhaust
 * it and leave a later phase truncated while the run still resolves — a failure
 * shaped exactly like success. Each cap is far beyond any real tenant and
 * exists only so a server that returned done=false without advancing its cursor
 * cannot spin forever.
 */
const MAX_BATCHES_PER_PHASE = 20000;

/**
 * Drive all three phases to completion, in the order the server dictates via
 * `next_phase`. Resolves only once the final phase reports done, so the caller
 * may treat resolution as "the whole run finished".
 */
export async function runDomainClassificationRepair(
  brandId: string,
  onProgress?: (p: DomainClassificationProgress) => void,
): Promise<DomainClassificationRepairResult> {
  const totals: DomainClassificationRepairResult = {
    scanned: 0,
    reclassified: 0,
    overridden: 0,
    responses_reanalyzed: 0,
    responses_failed: 0,
    periods_rescored: 0,
    periods_failed: 0,
    score_rows_written: 0,
    phasesRun: [],
    batches: 0,
  };

  // The server names the first phase too; start where it starts.
  let phase: DomainClassificationPhase | undefined = 'reclassify';
  const seen = new Set<DomainClassificationPhase>();

  while (phase) {
    if (seen.has(phase)) break; // a server that pointed back at a finished phase
    seen.add(phase);
    totals.phasesRun.push(phase);
    const phaseIndex = totals.phasesRun.length;

    let strCursor: string | null = null;
    let idCursor: string | null = null;
    let next: DomainClassificationPhase | undefined;

    for (let i = 0; i < MAX_BATCHES_PER_PHASE; i++) {
      let done = false;
      let remaining = 0;

      if (phase === 'reclassify') {
        const b = await reclassifyBatch(strCursor, idCursor);
        // `?? 0` on every accumulation: a bundle served against a different-vintage
        // server mid-deploy can be missing a field, and `undefined + n` is NaN —
        // which would surface as "NaN domains corrected" rather than as an error.
        totals.scanned += b.scanned ?? 0;
        totals.reclassified += b.reclassified ?? 0;
        totals.overridden += b.overridden ?? 0;
        strCursor = b.last_created_at ?? null;
        idCursor = b.last_id ?? null;
        done = b.done;
        remaining = b.remaining ?? 0;
        next = b.next_phase;
      } else if (phase === 'reanalyze') {
        const b = await reanalyzeBatch(brandId, idCursor);
        totals.responses_reanalyzed += b.responses_reanalyzed ?? 0;
        totals.responses_failed += b.responses_failed ?? 0;
        idCursor = b.last_id ?? null;
        done = b.done;
        remaining = b.remaining ?? 0;
        next = b.next_phase;
      } else {
        const b = await rescoreBatch(brandId, strCursor, idCursor);
        totals.periods_rescored += b.periods_rescored ?? 0;
        totals.periods_failed += b.periods_failed ?? 0;
        totals.score_rows_written += b.score_rows_written ?? 0;
        strCursor = b.last_period ?? null;
        idCursor = b.last_brand_id ?? null;
        done = b.done;
        remaining = b.periods_remaining ?? 0;
        next = b.next_phase;
      }

      totals.batches += 1;
      onProgress?.({
        phase,
        phaseIndex,
        reclassified: totals.reclassified,
        responsesReanalyzed: totals.responses_reanalyzed,
        periodsRescored: totals.periods_rescored,
        remaining,
      });

      if (done) break;
      // A phase that reports not-done while advancing no cursor would loop
      // forever; the batch cap above is the backstop.
    }

    phase = next;
  }

  return totals;
}
